import {
    compose,
    curry,
    replace,
    sort,
    prop,
    join,
    sum,
    map,
    __,
    filter,
    reject,
    unnest,
    propSatisfies,
    ascend,
    mapAccum,
    take,
    uniq,
    over,
    toUpper,
    lensIndex,
    propOr,
    equals,
    isNil,
    isEmpty,
    anyPass,
    any,
    pipe
} from 'ramda';

import { routeGroups } from './RouteGroups';

// misc helpers
const getNonEmptySegments = x => x.filter(s => s.value);
export const filterTruthy = x => x.filter(Boolean);

export const toTitle = compose(
    join(''),
    over(lensIndex(0), toUpper)
  );

// props
const propPoints = prop('points');
export const propValue = prop('value');
export const propTerm = prop('term');
export const propTerms = prop('terms');
export const propLength = prop('length');

// format, replace
export const replaceSpacesWithDashes = compose(replace(/\s/g, '-'));
export const joinWithCommas = join(', ');
export const joinWithDashes = join('-');
export const joinWithAmpersand = join('&');
export const replaceSlashDash = replace(/\/-/g, '/');
export const replaceDashSlash = replace(/-\//g, '/');
export const replaceCommaAmpersand = replace(/,/g, ' &');
export const replaceAmpersandWithAnd = replace(/&/g, 'and');
export const replacePeriodWithDash = replace(/\./g, '-');
export const replaceSlashWithDash = replace(/\//g, '-');
export const pluralizeCylinder = replace(/-cylinder/g, '-cylinders');
export const mergePlugIn = replace(/plug-in/g, 'plugin');
export const formatYear = (year, isYearRange) => isYearRange ? `${year.min}-${year.max}` : `${year.min}`;

// test hook generation
const removeDashes = compose(replace(/-/g, ''));
const removeSpaces = compose(replace(/\s/g, ''));
export const formatCrumbTestHook = compose(removeSpaces, removeDashes);

// aliases
export const formatMake = replaceSpacesWithDashes;
export const formatModel = replaceSpacesWithDashes;

// sorting, ordering
const sortAlpha = ascend;
const arrayAlphaSort = sort(sortAlpha);
const sortByModelName = (a, b) => a.model.localeCompare(b.model);
export const sortLocale = (a, b) => a.localeCompare(b);
export const withSortLocale = sort(sortLocale);
export const sortTakeTwoJoinDash = compose(joinWithDashes, take(2), withSortLocale);
export const sortAlphaKeys = compose(arrayAlphaSort, Object.keys);
export const sortAcummulatedTerms = obj => mapAccum((a, b) => [a, obj[b].terms], [], sortAlphaKeys(obj));

// map prop
const mapPoints = map(propPoints);
export const mapTerm = map(propTerm);
export const takeValues = map(propValue);
export const mapReplaceSpaces = map(replaceSpacesWithDashes);

// map nested
const sortByTermLocale = (a, b) => a.term.localeCompare(b.term);
export const mapNestedTerm = map(mapTerm);
export const withSortTermLocale = sort(sortByTermLocale);
export const sortEachByTerm = map(withSortTermLocale);
export const takeTwoDashTerm = compose(joinWithDashes, mapReplaceSpaces, mapTerm, take(2), withSortTermLocale);
export const takeTwoDashTermUpper = compose(joinWithDashes, mapReplaceSpaces, map(toUpper), mapTerm, take(2), withSortTermLocale);
export const takeTwoDashTermTitleCase = compose(joinWithDashes, mapReplaceSpaces, map(toTitle), mapTerm, take(2), withSortTermLocale);

// boolean
export const hasLength = arr => arr && arr.length;
const lengthGreaterThanOne = propSatisfies(x => x > 1, 'length');
const isMultiSelectKeys = compose(lengthGreaterThanOne, Object.keys);
const isMultiSelectArray = compose(lengthGreaterThanOne);
const hasTruthyValues = any(
  pipe(
    propValue,
    Boolean
  )
);

export const matchesGroup = group => x => x.group && x.group === group;

// Pipelines
export const unnestRemoveEmpty = compose(getNonEmptySegments, unnest);
export const translateOptions = compose(unnest, mapNestedTerm, sortEachByTerm);
export const isTaxonomyGroup = matchesGroup(routeGroups.taxonomy);
export const sortMapJoinTerms = compose(joinWithDashes, mapReplaceSpaces, mapTerm, withSortTermLocale);

// Postprocessing, Points
export const curryComposeFour = curry((f1, f2, f3, f4) => compose(f1, f2, f3, f4));
const sumPointsBy = curryComposeFour(sum, mapPoints, __, unnestRemoveEmpty);
const sumPoints = f => sumPointsBy(filter(f));
export const sumPointsNot = f => sumPointsBy(reject(f));
export const sumPointsByGroup = group => sumPoints(matchesGroup(group));
export const countColorsPoints = sumPointsByGroup(routeGroups.colors);
export const countTrimsPoints = sumPointsByGroup(routeGroups.trims);
export const countLocationPoints = sumPointsByGroup(routeGroups.location);
export const countNonTaxonomyPoints = sumPointsNot(isTaxonomyGroup);
export const countFeaturesPoints = sumPointsByGroup(routeGroups.features);
export const countOptionsPoints = sumPointsByGroup(routeGroups.options);


export const getNonEmptyUrl = filterTruthy;

// Preprocessing
const sortMakes = compose(sort(sortLocale), map(prop('make')), propOr([], 'models'));
export const getUniqueMakes = compose(uniq, map(prop('make')), propOr([], 'models'));
const getFirstMake = modelIds => sortMakes(modelIds)[0];
const defaultSortBy = 'MostPopular';

const getComparatorFirstMakeWhereEntireSelected = rulesState => x=>x.make===rulesState.firstMake && x.isEntireMakeSelected

const isMakeAllModelsSelected = (modelIds, rulesState) => {
    return rulesState.firstMake
        && modelIds
        && modelIds.models
        && modelIds.models.filter(getComparatorFirstMakeWhereEntireSelected(rulesState)).length > 0;
};

const reviewFirstMake = (modelIds, rulesState) => {
    const models = modelIds.models.filter(x=> x.make === rulesState.firstMake);

    return {
        models,
        isNoModel: models.length === 0,
        isSingleModel: models.length === 1,
        isAllModelSelected: isMakeAllModelsSelected(modelIds, rulesState)
    };
};

const reviewTaxonomy = (modelIds, rulesState) => {
    const {
        models,
        isNoModel,
        isSingleModel,
        isAllModelSelected
    } =  reviewFirstMake(modelIds, rulesState);

    const uniqueMakes = getUniqueMakes(modelIds);

    return {
        isNoModel,
        isSingleModel,
        isMultiMake: uniqueMakes && lengthGreaterThanOne(uniqueMakes),
        isMultiModelSelect: isMultiSelectArray(models),
        isAllModelSelected
    };
};

const isTaxonomyUndecided = rulesState => value => {
    if (!rulesState.hasTaxonomy()) {
        return value;
    }

    return false;
};

const setTaxonomyFlags = (modelIds, rulesState) => {
    const mm = modelIds.models.filter(x=> x.make === rulesState.firstMake);
    rulesState.taxonomyReview = reviewTaxonomy(modelIds, rulesState);

    const maybeSet = isTaxonomyUndecided(rulesState);

    rulesState.flags.isMultiMake = rulesState.taxonomyReview.isMultiMake;
    rulesState.flags.isNoModels = maybeSet(rulesState.taxonomyReview.isNoModel);
    rulesState.flags.isAllModels = maybeSet(rulesState.taxonomyReview.isAllModelSelected);
    rulesState.flags.isMultiModel = maybeSet(rulesState.taxonomyReview.isMultiModelSelect);
    rulesState.flags.isSingleModel = maybeSet(rulesState.taxonomyReview.isSingleModel);

    if (rulesState.flags.isSingleModel || rulesState.flags.isMultiMake || rulesState.flags.isMultiModel) {
        const [firstModel] = sort(sortByModelName, mm);
        rulesState.firstModelName = firstModel && firstModel.model;
    }
};

const processMakes = (selectedFilters, rulesState) => {
    const {
        modelIds
    } = selectedFilters;

    rulesState.firstMake = getFirstMake(modelIds);

    if (rulesState.firstMake) {
        setTaxonomyFlags(modelIds, rulesState);
    } else {
        rulesState.flags.isNoMake = true;
        rulesState.flags.isNoModels = true;
    }
};

const isPopulated = (property = {}) => isMultiSelectKeys(property);
const isPopulatedArray = (property = []) => property.length > 0;

const isSpecialPriceBandCheck = rangeValue => {
    const priceBands = [15000, 20000, 25000];

    if (priceBands.includes(rangeValue.max) && !rangeValue.min) {
        return true;
    }

    return false;
}

const setPreProcessingFlags = (selectedFilters, rulesState) => {
    const {
        finance,
        price,
        mileage,
        mpg,
        bodyStyles,
        tagIds = {},
        attributes,
        textSearchInput
    } = selectedFilters;
    const { ranges } = price;
    const { terms: tagIdsTerms = [] } = tagIds;
    const priceFilter =reject(anyPass([isEmpty, isNil, equals({terms:[]})]))(selectedFilters);
    const hasPriceRange = !isEmpty(ranges)
    rulesState.flags.hasFinance = isPopulated(finance);
    rulesState.flags.hasPrice = isPopulated(price);
    rulesState.flags.isSpecialPriceBand = rulesState.flags.hasPrice && hasPriceRange  ? isSpecialPriceBandCheck(ranges[0]) : false;
    rulesState.flags.isPriceOnly = rulesState.flags.isSpecialPriceBand && Object.keys(priceFilter).length === 1
    rulesState.flags.priceBandMaxValue = rulesState.flags.isPriceOnly ? ranges[0].max: 0;
    rulesState.flags.hasMileage = isPopulated(mileage);
    rulesState.flags.hasMpg = isPopulated(mpg);
    rulesState.flags.isMultiBodyStyle = isMultiSelectArray(bodyStyles.terms);
    rulesState.flags.hasAttributes = isPopulatedArray(attributes);
    rulesState.flags.hasTextSearchInput = isPopulatedArray(textSearchInput);
    rulesState.flags.hasTagIds = isPopulatedArray(tagIdsTerms);
};


const checkIfHasPagination = selectedPagination => selectedPagination.page > 1;
const checkIfHasSort = selectedSort => selectedSort !== defaultSortBy;

const checkIfIsFreeDelivery = isFreeDelivery =>
  isPopulated(isFreeDelivery) &&
  isFreeDelivery.entries &&
  hasTruthyValues(isFreeDelivery.entries);

const processFreeDelivery = (selectedFilters, rulesState) => {
    rulesState.flags.isFreeDelivery = checkIfIsFreeDelivery(selectedFilters.isFreeDelivery);
};

const processPagination = (selectedFiltersForApi, rulesState) => {
    rulesState.flags.hasPagination = checkIfHasPagination(selectedFiltersForApi.pagination || {});
};

const processSort = (selectedFiltersForApi, rulesState) => {
    rulesState.flags.hasSort = checkIfHasSort(selectedFiltersForApi.sortBy || defaultSortBy);
};

const needsMap = option => option && option.terms;



const mapTermsAsDisplayNameWithTransform = transform => option => {
    if(!needsMap(option)){
        return option;
    }

    const {terms} = option;
    return terms.map(x=>({displayName: transform(x.term)}));
};

export const mapTermsAsDisplayName = mapTermsAsDisplayNameWithTransform(toTitle);
export const mapTermsAsDisplayNameUpper = mapTermsAsDisplayNameWithTransform(toUpper);

export const preProcessing = ({ selectedFilters, rulesState }) => {
    setPreProcessingFlags(selectedFilters, rulesState);

    processFreeDelivery(selectedFilters, rulesState);
    processPagination(rulesState.config.selectedFiltersForApi || {}, rulesState);
    processSort(rulesState.config.selectedFiltersForApi || {}, rulesState);

    selectedFilters.bodyStyles.terms.forEach(x=>{
        if (x.term === 'Suv') {
            x.term = 'SUV'
        }
    });

    processMakes(selectedFilters, rulesState);
};

